
import {Component} from 'vue-property-decorator';
import * as echarts from 'echarts';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import {GobiColor} from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import {TableItemFormatter} from '../../elements/charts/helpers/tableItemFormatter';
import moment from 'moment/moment';
import {getTableName, TIMEZONE_OFFSET_MS} from '@/worker/db/sqlHelpers';

@Component({
    components: {
        StackedBarChartFilterable,
        TableCollapseToggle,
        SelectFilter,
    },
})
export default class BpRevenuePurchasesExpensesBarChart extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['transactions'];
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'projects'
    > = ['dateRange'];

    public xAxisData: string[] = [];
    public series: echarts.ECharts[] | any = [];
    public periodTotalData: Array<{
        net_sales: number;
        net_purchases: number;
        expenses: number;
    }> = [];
    public periodTotalFields: any[] = [];
    public selectedTimeframe: string = this.timeframe[0];

    public get timeframe() {
        const timeframeList = [
            'Monthly',
            '3 months',
        ];
        return timeframeList;
    }

    public get currencySymbol() {
        return gdbx.currencySymbol;
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            selectedAsOfDate,
            selectedProjects,
            selectedTimeframe,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            selectedAsOfDate,
            selectedProjects,
            selectedTimeframe,
        ]);
    }

    public async expensiveCalc() {
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const table = getTableName('transactions');
        const range = this.selectedDateRange;

        let projectFilter = '';
        if (this.selectedProjects.length > 0) {
            const list = this.selectedProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            projectFilter = ` AND "PROJECT" IN (${list})`;
        }

        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded(['transactions']);
        this.checkInterrupted(jobId);

        this.generateLoadingText(0.3);

        const monthlyResult = await queryHead.rawQuery(`
            SELECT
                EXTRACT(YEAR FROM EPOCH_MS(TRY_CAST("DOCDATE" AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as year,
                EXTRACT(MONTH FROM EPOCH_MS(TRY_CAST("DOCDATE" AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as month,
                SUM(CASE WHEN "ACCTYPE" IN ('SL','SA') THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as net_sales,
                SUM(CASE WHEN "ACCTYPE" = 'CO' THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as cogs,
                SUM(CASE WHEN "ACCTYPE" = 'EP' THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as expenses
            FROM ${table}
            WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${range[0]} AND ${range[1]}
                AND "ACCTYPE" IN ('SL','SA','CO','EP')
                ${projectFilter}
            GROUP BY year, month
            ORDER BY year, month
        `);
        this.checkInterrupted(jobId);

        // Build monthly arrays
        const startMoment = moment(range[0]).startOf('month');
        const endMoment = moment(range[1]).endOf('month');
        const monthlyMap: Record<string, any> = {};
        for (const row of monthlyResult) {
            monthlyMap[`${Number(row.year)}-${Number(row.month)}`] = row;
        }

        const salesDD: Array<{text: string; sum: number}> = [];
        const cogsDD: Array<{text: string; sum: number}> = [];
        const expensesDD: Array<{text: string; sum: number}> = [];
        const m = startMoment.clone();
        while (m.isSameOrBefore(endMoment, 'month')) {
            const key = `${m.year()}-${m.month() + 1}`;
            const row = monthlyMap[key];
            salesDD.push({text: m.format('MMM YYYY'), sum: row ? (Number(row.net_sales) || 0) : 0});
            cogsDD.push({text: m.format('MMM YYYY'), sum: row ? (Number(row.cogs) || 0) : 0});
            expensesDD.push({text: m.format('MMM YYYY'), sum: row ? (Number(row.expenses) || 0) : 0});
            m.add(1, 'month');
        }

        this.periodTotalFields = [
            {
                key: 'net_sales',
                formatter: TableItemFormatter.currencySplit,
                textAlign: 'center',
            },
            {
                key: 'net_purchases',
                formatter: TableItemFormatter.currencySplit,
                textAlign: 'center',
            },
            {
                key: 'expenses',
                formatter: TableItemFormatter.currencySplit,
                textAlign: 'center',
            },
        ];

        const netSales = {
            name: 'Net Revenue',
            data: [] as number[],
            type: 'bar',
            barWidth: '25%',
            barGap: '30%',
            large: true,
            smooth: true,
        };

        const cost = {
            name: 'Net Purchases',
            data: [] as number[],
            type: 'bar',
            barWidth: '25%',
            barGap: '30%',
            stack: 'spent',
            itemStyle: {color: GobiColor.COLOR3},
            smooth: true,
        };

        const expenses = {
            name: 'Expenses',
            data: [] as number[],
            type: 'bar',
            barWidth: '25%',
            barGap: '30%',
            stack: 'spent',
            itemStyle: {color: GobiColor.COLOR5},
            smooth: true,
        };

        this.xAxisData = [];

        netSales.data = salesDD.map((sales) => {
            return sales.sum * -1;
        });

        cost.data = cogsDD.map((cogs) => {
            return cogs.sum;
        });

        expenses.data = expensesDD.map((expense) => {
            return expense.sum;
        });

        this.xAxisData = salesDD.map((a) => {
            return a.text;
        });

        if (this.selectedTimeframe === '3 months') {
            const newArray1: any = [];
            const newArray2: any = [];
            const newArray3: any = [];

            // SL+SA combined: SL is credit (negative), SA is debit (positive). Net is negative, negate for display
            newArray1.push(
                ((salesDD[0].sum)
                    + (salesDD[1].sum)
                    + (salesDD[2].sum)) * -1,
            );

            newArray1.push(
                ((salesDD[3].sum)
                    + (salesDD[4].sum)
                    + (salesDD[5].sum)) * -1,
            );

            newArray1.push(
                ((salesDD[6].sum)
                    + (salesDD[7].sum)
                    + (salesDD[8].sum)) * -1,
            );

            newArray1.push(
                ((salesDD[9].sum)
                    + (salesDD[10].sum)
                    + (salesDD[11].sum)) * -1,
            );

            newArray2.push(
                (cogsDD[0].sum)
                + (cogsDD[1].sum)
                + (cogsDD[2].sum),
            );

            newArray2.push(
                (cogsDD[3].sum)
                + (cogsDD[4].sum)
                + (cogsDD[5].sum),
            );

            newArray2.push(
                (cogsDD[6].sum)
                + (cogsDD[7].sum)
                + (cogsDD[8].sum),
            );

            newArray2.push(
                (cogsDD[9].sum)
                + (cogsDD[10].sum)
                + (cogsDD[11].sum),
            );

            newArray3.push(
                (expensesDD[0].sum)
                + (expensesDD[1].sum)
                + (expensesDD[2].sum),
            );

            newArray3.push(
                (expensesDD[3].sum)
                + (expensesDD[4].sum)
                + (expensesDD[5].sum),
            );

            newArray3.push(
                (expensesDD[6].sum)
                + (expensesDD[7].sum)
                + (expensesDD[8].sum),
            );

            newArray3.push(
                (expensesDD[9].sum)
                + (expensesDD[10].sum)
                + (expensesDD[11].sum),
            );

            netSales.data = newArray1;
            cost.data = newArray2;
            expenses.data = newArray3;

            // Labels for oldest-first order: [0-2]=Q1, [3-5]=Q2, [6-8]=Q3, [9-11]=Q4
            this.xAxisData = [
                `${salesDD[0].text} - ${salesDD[2].text}`,
                `${salesDD[3].text} - ${salesDD[5].text}`,
                `${salesDD[6].text} - ${salesDD[8].text}`,
                `${salesDD[9].text} - ${salesDD[11].text}`,
            ];
        }

        if (this.selectedTimeframe === '6 months') {
            const newArray1: any = [];
            const newArray2: any = [];
            const newArray3: any = [];

            // SL+SA combined: SL is credit (negative), SA is debit (positive). Net is negative, negate for display
            newArray1.push(
                ((salesDD[0].sum)
                    + (salesDD[1].sum)
                    + (salesDD[2].sum)
                    + (salesDD[3].sum)
                    + (salesDD[4].sum)
                    + (salesDD[5].sum)) * -1,
            );

            newArray1.push(
                ((salesDD[6].sum)
                    + (salesDD[7].sum)
                    + (salesDD[8].sum)
                    + (salesDD[9].sum)
                    + (salesDD[10].sum)
                    + (salesDD[11].sum)) * -1,
            );

            newArray2.push(
                (cogsDD[0].sum)
                + (cogsDD[1].sum)
                + (cogsDD[2].sum)
                + (cogsDD[3].sum)
                + (cogsDD[4].sum)
                + (cogsDD[5].sum),
            );

            newArray2.push(
                (cogsDD[6].sum)
                + (cogsDD[7].sum)
                + (cogsDD[8].sum)
                + (cogsDD[9].sum)
                + (cogsDD[10].sum)
                + (cogsDD[11].sum),
            );

            newArray3.push(
                (expensesDD[0].sum)
                + (expensesDD[1].sum)
                + (expensesDD[2].sum)
                + (expensesDD[3].sum)
                + (expensesDD[4].sum)
                + (expensesDD[5].sum),
            );

            newArray3.push(
                (expensesDD[6].sum)
                + (expensesDD[7].sum)
                + (expensesDD[8].sum)
                + (expensesDD[9].sum)
                + (expensesDD[10].sum)
                + (expensesDD[11].sum),
            );

            netSales.data = newArray1;
            cost.data = newArray2;
            expenses.data = newArray3;

            // Labels for oldest-first order: [0-5]=1st half, [6-11]=2nd half
            this.xAxisData = [
                `${salesDD[0].text} - ${salesDD[5].text}`,
                `${salesDD[6].text} - ${salesDD[11].text}`,
            ];
        }

        this.periodTotalData = [
            {
                net_sales: netSales.data.reduce((a, b) => a + b, 0),
                net_purchases: cost.data.reduce((a, b) => a + b, 0),
                expenses: expenses.data.reduce((a, b) => a + b, 0),
            },
        ];

        this.series = [netSales, cost, expenses];

        this.saveHistory(
            'series',
            'xAxisData',
            'periodTotalData',
            'periodTotalFields',
        );
    }

    public onHovering(value: boolean, filterId: string) {
        this.$emit('hovering', value, filterId);
    }

    public onEditing(value: boolean, filterId: string) {
        this.$emit('editing', value, filterId);
    }

}
